import { useEffect, useRef, useState } from "react";
import { Bell, Check, CheckCheck, Info } from "lucide-react";
import { api } from "@/lib/api";

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const wrapRef = useRef(null);

  const load = async () => {
    try { const res = await api.get("/notifications"); setItems(res.data); }
    catch { /* ignore */ }
  };

  const toggle = () => {
    setOpen((o) => {
      if (!o) load();  // refresh on open
      return !o;
    });
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const unread = items.filter((i) => !i.read).length;

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };
  const markOne = async (id) => {
    await api.post(`/notifications/${id}/read`);
    load();
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        data-testid="notifications-btn"
        onClick={toggle}
        className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors"
      >
        <Bell className="w-5 h-5 text-slate-700" />
        {unread > 0 && (
          <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[10px] flex items-center justify-center rounded-full bg-blue-600 text-white font-bold">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notifications-panel" className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div className="font-display font-bold text-slate-900">Notifications</div>
            {unread > 0 && (
              <button data-testid="mark-all-read" onClick={markAll} className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1">
                <CheckCheck className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">No notifications yet</div>
            ) : (
              items.slice(0, 20).map((n) => (
                <div key={n.id} data-testid={`notif-${n.id}`} className={`px-4 py-3 border-b border-slate-50 flex gap-2 ${n.read ? "" : "bg-blue-50/40"}`}>
                  <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    n.kind === "success" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"
                  }`}>
                    <Info className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-900 line-clamp-1">{n.title}</div>
                    <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>
                    <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                  </div>
                  {!n.read && (
                    <button data-testid={`read-${n.id}`} onClick={() => markOne(n.id)} className="text-slate-400 hover:text-blue-600">
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
